import React, { useCallback, useMemo, useState } from 'react'

import classNames from 'clsx'
import i18n from 'i18n-literally'
import PropTypes from 'prop-types'

import { CalendarToday } from '@mui/icons-material'
import { DatePicker } from '@mui/x-date-pickers'

import { datePropType, stylePropType } from '~/src/Lib/PropTypes'
import { EMPTY_OBJECT, formattedDate, getDateTime } from '~/src/Lib/Utils'
import Button from '~/src/UI/Shared/Button'
import TextField from '~/src/UI/Shared/Form/TextField'

import Null from '../Null'

const noop = () => {}
const renderDay = (day, selectedDate, isInCurrentMonth, dayComponent) => (
  isInCurrentMonth ? dayComponent : React.cloneElement(dayComponent, { 'aria-hidden': true })
)

/**
 * A datepicker field that can be rendered as a text field or a button. Both open a datepicker modal on click/touch.
 * @component
 * @alias DatePicker
 * @named_export DatePickerComponent
 * @composes
 * from: '@mui/x-date-pickers/Datepicker'
 * link: https://material-ui-pickers.dev/api/DatePicker
 * @example
 * import { formattedDate } from '~/src/Lib/Utils'
 * {() => {
 *   const [date, setDate] = React.useState('2012-01-05T07:00:00.000-0700')
 *   return (
 *     <Box display="flex" alignItems="center" style={{ gap: '1rem' }}>
 *       <T>Date: {formattedDate(date, 'TINY_DATETIME')}</T>
 *       <DatePicker value={date} onChange={setDate} />
 *       <DatePicker button value={date} onChange={setDate} />
 *     </Box>
 *   )
 * }}
 */
export const DatePickerComponent = ({
  button = false,
  className = '',
  clearable = true,
  // DialogProps: dialogPropsProp = EMPTY_OBJECT,
  error: propsError = undefined,
  fullWidth = false,
  helperText: propsHelperText = undefined,
  label = null,
  margin = 'none',
  name = null,
  slotProps: slotPropsProp = EMPTY_OBJECT,
  value = '',
  onChange: propsOnChange,
  ...props
}) => {
  const onChange = useCallback(
    date => propsOnChange(name ? { [name]: date } : date),
    [name, propsOnChange]
  )
  const [isOpen, setIsOpen] = useState(false)
  const dateValue = value ? getDateTime(value) : null
  const buttonProps = useMemo(() => {
    if (!button) return EMPTY_OBJECT
    return {
      open: isOpen,
      onOpen: () => setIsOpen(true),
      onClose: () => setIsOpen(false),
      TextFieldComponent: Null
    }
  }, [button, isOpen])
  // This doesn't work. We need to figure out how to pass the props to the dialog.
  // const dialogProps = useMemo(() => ({
  //   ...dialogPropsProp,
  //   'aria-label': i18n`${transform(label || name || 'anonymous', ['underscore', 'humanize']).toLowerCase()} date picker`,
  // }), [dialogPropsProp, label, name])

  // eslint-disable-next-line react/no-unstable-nested-components
  const TextFieldComponent = useMemo(() => ({
    clearable: ignore,
    error,
    helperText,
    isOpen: ignore2,
    onChange: tfOnChange = noop,
    ...tfProps
  }) => (
    <TextField
      {...tfProps}
      margin={margin}
      error={(error && helperText) || propsError}
      helperText={helperText || propsHelperText}
      fullWidth={fullWidth}
      onChange={tfOnChange}
    />
  ), [propsError, propsHelperText, fullWidth, margin])

  return (
    <>
      <DatePicker
        data-testid="date-picker"
        autoOk
        className={classNames(className, 'aroya__date-picker')}
        clearable={Boolean(clearable && value)}
        format="D"
        TextFieldComponent={TextFieldComponent}
        placeholder="Press space to open calendar"
        orientation="portrait"
        isOpen={isOpen}
        {...buttonProps}
        {...props}
        slotProps={{
          ...slotPropsProp,
          openPickerButton: {
            'aria-label': ([
              i18n`Choose ${
                label || name ? `"${label || name}"` : i18n`date`
              }${!(label || name || '').match(new RegExp(i18n`date`, 'i')) ? i18n` date` : ''}`,
              dateValue && dateValue.isValid ? i18n`selected date is ${formattedDate(dateValue, 'SHORT_DATE')}` : null
            ]).filter(Boolean).join(', ')
          },
        }}
        label={label}
        name={name}
        sx={{
          '&.aroya__date-picker:not(:has(label)) input': {
            paddingTop: '1rem',
            paddingBottom: '1rem',
          }
        }}
        value={dateValue}
        onChange={onChange}
        renderDay={renderDay}
      />
      {button && (
        <Button
          color="primary"
          endIcon={<CalendarToday />}
          onClick={() => {
            setIsOpen(true)
          }}
          variant="text"
        >
          {formattedDate(dateValue, 'LONG_DATE')}
        </Button>
      )}
    </>
  )
}
DatePickerComponent.displayName = 'DatePicker'
DatePickerComponent.propTypes = {
  clearable: PropTypes.bool,
  fullWidth: PropTypes.bool,
  error: PropTypes.node,
  helperText: PropTypes.node,
  label: PropTypes.string,
  button: PropTypes.bool,
  margin: PropTypes.oneOf(['dense', 'none', 'normal']),
  name: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, datePropType]),
  onChange: PropTypes.func.isRequired,
  style: stylePropType,
}

export const FormikDatePicker = React.memo(props => {
  const { field, form, alwaysShowError, ...passthru } = props
  const { setFieldTouched, setFieldValue } = form
  const onChange = useCallback(
    ({ [field.name]: value }) => {
      setFieldValue(field.name, value)
      setFieldTouched(field.name)
    },
    [field.name, setFieldTouched, setFieldValue]
  )

  const { error, touched } = form.getFieldMeta(field.name)

  return (
    <DatePickerComponent
      {...passthru}
      {...field}
      error={form.dirty && (touched || alwaysShowError) && error}
      onChange={onChange}
    />
  )
})

const Memoized = React.memo(DatePickerComponent)

export default Memoized
